import React, { useState, useEffect } from 'react';
import { X, CreditCard, Edit3, Bell, ShieldCheck, GraduationCap, AlertTriangle } from 'lucide-react';

const FeeStudentDrawer = ({
  isOpen,
  onClose,
  student,
  fee,
  feeService,
  orgId,
  onEditFee,
  onCollectPayment,
  onAlertParent,
  onViewHistory,
  onGenerateClearanceCert
}) => {
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);

  useEffect(() => {
    if (isOpen && student?.studentId) {
      setLoadingTx(true);
      feeService.getTransactionHistory(student.studentId, orgId)
        .then((txs) => setTransactions(Array.isArray(txs) ? txs : []))
        .catch((err) => {
          console.error('Failed to load transactions for drawer:', err);
          setTransactions([]);
        })
        .finally(() => setLoadingTx(false));
    }
  }, [isOpen, student?.studentId, feeService, orgId]);

  if (!isOpen || !student) return null;

  const isVoided = fee?.status === 'VOIDED';

  const total = fee?.totalFees != null
    ? Number(fee.totalFees)
    : ((fee?.items && fee.items.length > 0)
        ? fee.items.reduce((s, i) => s + (i.netAmount || i.originalAmount || 0), 0)
        : 0);

  const paid = fee?.paidFees != null ? Number(fee.paidFees) : 0;
  const concession = fee?.concessionAmount != null ? Number(fee.concessionAmount) : 0;
  const penalty = fee?.penaltyAmount != null ? Number(fee.penaltyAmount) : 0;
  const due = fee?.pendingFee != null
    ? Number(fee.pendingFee)
    : Math.max(0, total - concession + penalty - paid);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        backdropFilter: 'blur(3px)',
        zIndex: 1050,
        display: 'flex',
        justifyContent: 'flex-end'
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '440px',
          height: '100%',
          backgroundColor: '#ffffff',
          boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1051
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: isVoided ? '#fef2f2' : '#f8fafc'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: isVoided ? '#fee2e2' : '#eff6ff',
                color: isVoided ? '#991b1b' : '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '800',
                fontSize: '15px'
              }}
            >
              {student.name ? student.name.charAt(0).toUpperCase() : 'S'}
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>
                {student.name}
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>
                  ID: {student.studentId}
                </span>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>•</span>
                <span style={{ fontSize: '11px', color: '#334155', fontWeight: '600' }}>
                  {student.className || 'Class'} {student.section ? `(Sec ${student.section})` : ''}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#64748b',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Drawer Body */}
        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Status Alert if Voided */}
          {isVoided && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 12px', color: '#991b1b', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShieldCheck size={16} />
              <div>
                <strong>VOIDED RECORD:</strong> This fee entry has been cancelled and does not represent an active liability.
              </div>
            </div>
          )}

          {/* Financial Summary 2x2 Grid */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.3px' }}>
              Financial Summary
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px 10px' }}>
                <div style={{ fontSize: '10.5px', color: '#64748b' }}>Total Fee</div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', marginTop: '2px' }}>
                  ₹{total.toLocaleString('en-IN')}
                </div>
              </div>

              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px 10px' }}>
                <div style={{ fontSize: '10.5px', color: '#166534' }}>Paid Amount</div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#166534', marginTop: '2px' }}>
                  ₹{paid.toLocaleString('en-IN')}
                </div>
              </div>

              {concession > 0 && (
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px 10px' }}>
                  <div style={{ fontSize: '10.5px', color: '#059669' }}>Concession / Discount</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#059669', marginTop: '2px' }}>
                    -₹{concession.toLocaleString('en-IN')}
                  </div>
                </div>
              )}

              {penalty > 0 && (
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px 10px' }}>
                  <div style={{ fontSize: '10.5px', color: '#dc2626' }}>Late Penalty</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#dc2626', marginTop: '2px' }}>
                    +₹{penalty.toLocaleString('en-IN')}
                  </div>
                </div>
              )}

              <div
                style={{
                  gridColumn: concession > 0 || penalty > 0 ? 'span 2' : 'span 2',
                  background: due > 0 ? '#fef2f2' : '#f0fdf4',
                  border: due > 0 ? '1px solid #fecaca' : '1px solid #bbf7d0',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: due > 0 ? '#991b1b' : '#166534', textTransform: 'uppercase' }}>
                    Outstanding Balance
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: due > 0 ? '#7f1d1d' : '#14532d' }}>
                    ₹{due.toLocaleString('en-IN')}
                  </div>
                </div>
                <div
                  style={{
                    background: due > 0 ? '#dc2626' : '#16a34a',
                    color: '#ffffff',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '700'
                  }}
                >
                  {due > 0 ? 'DUE PENDING' : 'FULLY CLEARED'}
                </div>
              </div>
            </div>
          </div>

          {/* Structure Breakdown Items */}
          {fee?.items && fee.items.length > 0 && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.3px' }}>
                Fee Components Breakdown
              </div>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                {fee.items.map((it, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '7px 10px',
                      fontSize: '12px',
                      borderBottom: idx === fee.items.length - 1 ? 'none' : '1px solid #f1f5f9',
                      background: idx % 2 === 0 ? '#ffffff' : '#f8fafc'
                    }}
                  >
                    <span style={{ fontWeight: '500', color: '#334155' }}>{it.feeType}</span>
                    <span style={{ fontWeight: '700', color: '#0f172a' }}>
                      ₹{Number(it.netAmount || it.amount || it.originalAmount || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Installments Schedule */}
          {fee?.installments && fee.installments.length > 0 && (
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.3px' }}>
                Installment Terms Schedule
              </div>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                {fee.installments.map((inst, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '7px 10px',
                      fontSize: '12px',
                      borderBottom: idx === fee.installments.length - 1 ? 'none' : '1px solid #f1f5f9',
                      background: idx % 2 === 0 ? '#ffffff' : '#f8fafc'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '600', color: '#1e293b' }}>{inst.installmentName}</div>
                      {inst.dueDate && (
                        <div style={{ fontSize: '10.5px', color: '#64748b' }}>
                          Due: {new Date(inst.dueDate).toLocaleDateString('en-IN')}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: '700', color: '#0f172a' }}>
                        ₹{Number(inst.amount || 0).toLocaleString('en-IN')}
                      </div>
                      <span
                        style={{
                          fontSize: '9.5px',
                          fontWeight: '700',
                          color: inst.status === 'PAID' ? '#166534' : '#dc2626'
                        }}
                      >
                        {inst.status === 'PAID' ? 'PAID' : 'PENDING'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Transaction Logs */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Recent Payments ({transactions.length})</span>
              <button
                type="button"
                onClick={() => onViewHistory(student)}
                style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: '11px', fontWeight: '600', cursor: 'pointer', padding: 0 }}
              >
                View All
              </button>
            </div>

            {loadingTx ? (
              <div style={{ textAlign: 'center', padding: '12px', fontSize: '12px', color: '#64748b' }}>
                Loading receipts...
              </div>
            ) : transactions.length === 0 ? (
              <div style={{ border: '1px dashed #cbd5e1', borderRadius: '6px', padding: '12px', textAlign: 'center', fontSize: '11.5px', color: '#94a3b8' }}>
                No payment transactions recorded yet.
              </div>
            ) : (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                {transactions.slice(0, 4).map((tx, idx) => (
                  <div
                    key={tx.transactionId || idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 10px',
                      fontSize: '12px',
                      borderBottom: idx === Math.min(transactions.length, 4) - 1 ? 'none' : '1px solid #f1f5f9'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '600', color: '#1e293b' }}>
                        Receipt #{tx.receiptNumber || tx.transactionId?.slice(-6) || 'RCPT'}
                      </div>
                      <div style={{ fontSize: '10.5px', color: '#64748b' }}>
                        {tx.paymentDate ? new Date(tx.paymentDate).toLocaleDateString('en-IN') : 'Recent'} • {tx.paymentMode || 'Cash'}
                      </div>
                    </div>
                    <div style={{ fontWeight: '800', color: '#166534', fontSize: '13px' }}>
                      ₹{Number(tx.paidAmount || 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Clearance Safeguard / Status Banner */}
        {!isVoided && (
          <div style={{ padding: '8px 20px', borderTop: '1px solid #e2e8f0', background: due > 0 ? '#fffbeb' : (fee && total > 0 ? '#f0fdf4' : '#f8fafc') }}>
            {due > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: '#b45309', fontWeight: '600' }}>
                <AlertTriangle size={13} color="#d97706" />
                <span>⚠️ Pending dues: ₹{Number(due).toLocaleString('en-IN')}. Clearance certificate cannot be issued.</span>
              </div>
            ) : (fee && total > 0) ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: '#15803d', fontWeight: '700' }}>
                  <ShieldCheck size={14} color="#16a34a" />
                  <span>✅ 100% Fees Cleared • No Dues</span>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={() => {
                    onClose();
                    if (onGenerateClearanceCert) onGenerateClearanceCert(student, fee);
                  }}
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    padding: '2px 8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    borderRadius: '4px'
                  }}
                >
                  <GraduationCap size={13} /> Generate Clearance Cert
                </button>
              </div>
            ) : (
              <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                ℹ️ No fee assessment found for this academic year.
              </div>
            )}
          </div>
        )}

        {/* Drawer Quick Action Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e2e8f0',
            background: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {!isVoided && due > 0 && (
            <button
              type="button"
              className="btn btn-warning btn-sm"
              onClick={() => {
                onClose();
                onAlertParent(student, due);
              }}
              style={{
                flex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                height: '32px',
                fontSize: '11.5px',
                fontWeight: '600'
              }}
            >
              <Bell size={13} /> Alert
            </button>
          )}

          {!isVoided && due <= 0 && fee && total > 0 && (
            <button
              type="button"
              className="btn btn-success btn-sm"
              onClick={() => {
                onClose();
                if (onGenerateClearanceCert) onGenerateClearanceCert(student, fee);
              }}
              style={{
                flex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                height: '32px',
                fontSize: '11.5px',
                fontWeight: '700',
                backgroundColor: '#16a34a',
                borderColor: '#16a34a'
              }}
              title="Generate Official Fee Clearance Certificate"
            >
              <GraduationCap size={14} /> 🎓 Fee Clearance Cert
            </button>
          )}

          {!isVoided && fee && due > 0 && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                onClose();
                onCollectPayment(student, fee);
              }}
              style={{
                flex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                height: '32px',
                fontSize: '11.5px',
                fontWeight: '600'
              }}
            >
              <CreditCard size={13} /> Collect
            </button>
          )}

          <button
            type="button"
            className="btn btn-light btn-sm"
            onClick={() => {
              onClose();
              onEditFee(student, fee);
            }}
            style={{
              flex: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              height: '32px',
              fontSize: '11.5px',
              fontWeight: '600',
              border: '1px solid #cbd5e1',
              background: '#ffffff'
            }}
          >
            <Edit3 size={13} /> {isVoided ? 'Correct' : (fee ? 'Edit' : 'Assign')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeeStudentDrawer;
